import {type RoomClientState} from '../../../../shared/scripts/RoomClientState.ts'
import {createCheckboxIn, createDivIn, createImgIn, createSpanIn, setElementInnerHTML, setElementOnClick} from '../../../../shared/scripts/ui_utils/DomUtils.ts'
import {UIMRoomPlayersChatController} from './UIMRoomPlayersChatController.ts'
import {CA} from '../../../../shared/scripts/analytics/Analytics.ts'
import {type AjaxDataTypes} from '../../../../../shared/AjaxDataTypes.ts'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../../shared/entity_datas/EntityDatas.ts'
import {socket} from '../../../../shared/scripts/EntryShared.ts'
import {UIRoomWarningController, WarningType} from '../../../../shared/scripts/ui_html/room_warning/UIRoomWarningController.ts'
import {UIMRoomUserListController} from './UIMRoomUserListController.ts'
import {UIMDeviceHandler} from '../UIMDeviceHandler.ts'
import {LobbyImage} from '@colonist/types'
import {UIMInviteFriendsBottomDrawer} from '../../ui_popup/UIMInviteFriendsBottomDrawer.ts'
import {ClientUserState} from '../../../../shared/scripts/ClientUserState.ts'
import {UIGameSpriteHandler} from '../../../../shared/scripts/UIGameSpriteHandler.ts'
import {AudioManager, AudioType} from '@colonist/audio'

export class UIMRoomPlayersController {
    private readonly container: HTMLDivElement
    private roomState: RoomClientState

    private readonly playersContainer: HTMLDivElement
    readonly chatController: UIMRoomPlayersChatController
    private readonly bottomButtonsContainer: HTMLDivElement
    private sharingContainer: HTMLDivElement
    private inviteFriendsButton: HTMLDivElement

    private isHost: boolean
    private startGameButton: HTMLDivElement
    private readyCheckboxContainer: HTMLInputElement
    private copyLinkButton: HTMLDivElement

    private playersCount = 0
    private readonly userList: UIMRoomUserListController

    constructor(container: HTMLDivElement, roomState: RoomClientState) {
        this.container = container
        this.roomState = roomState
        this.isHost = this.roomState.isHost(socket.controller.session.id)
        this.playersContainer = createDivIn(this.container, 'm-room-players-container')
        this.chatController = new UIMRoomPlayersChatController(this.container)

        this.bottomButtonsContainer = createDivIn(this.container, 'm-room-players-bottom-buttons-container')

        this.playersCount = roomState.getSessionCount()
        this.userList = new UIMRoomUserListController(this.playersContainer)
        this.userList.load(roomState)

        this.handleSounds(roomState)
        this.renderBottomButtons()
    }

    private renderPlayerCards(roomState: RoomClientState) {
        this.playersCount = roomState.getSessionCount()
        this.userList.update(roomState)
    }

    private renderBottomButtons() {
        if(this.bottomButtonsContainer.hasChildNodes()) setElementInnerHTML(this.bottomButtonsContainer, {key: 'strings:colonistCommon.utils.emptyString'})
        this.createSharingContainer()
        this.renderInviteFriendsButton()
        this.renderCopyLinkButton()
    }

    private createSharingContainer() {
        this.sharingContainer = createDivIn(this.bottomButtonsContainer, 'm-room-players-sharing-container')
    }

    private renderInviteFriendsButton() {
        if(!ClientUserState.userState?.isLoggedIn) return
        this.inviteFriendsButton = createDivIn(this.sharingContainer, 'm-button-cta-secondary-orange')
        setElementOnClick(this.inviteFriendsButton, () => {
            const eventData: AjaxDataTypes.AnalyticData = {
                type: AnalyticEventType.ClickedInviteFriendButton,
                category: AnalyticEventCategory.MobileAnalytics,
            }
            void CA.sendAnalyticEventsWithMobileDimensions(eventData)
            new UIMInviteFriendsBottomDrawer()
        })
        setElementInnerHTML(this.inviteFriendsButton, {key: 'strings:roomPage.inviteFriends'})
    }

    private renderCopyLinkButton() {
        this.copyLinkButton = createDivIn(this.sharingContainer, 'm-button-cta-secondary-dark-overlay')
        createImgIn(this.copyLinkButton, LobbyImage.Share, '')

        setElementOnClick(this.copyLinkButton, () => this.onCopyLinkButtonClick(window.location.href))

        if(this.isHost) {
            this.startGameButton = createDivIn(this.bottomButtonsContainer, 'm-button-cta-secondary')
            setElementInnerHTML(this.startGameButton, {key: 'strings:roomPage.startGame'})
            setElementOnClick(this.startGameButton, () => UIRoomWarningController.runWithWarning(async() => {
                AudioManager.play(AudioType.Click)
                await UIGameSpriteHandler.continueAfterAssetsLoad()
                socket.roomClient.startGame()
            }, WarningType.StartGameVPExceededRecommendedLimit, this.roomState))
        } else {
            const readyContainer = createDivIn(this.bottomButtonsContainer, 'm-room-players-ready-container')
            createSpanIn(readyContainer, '', {key: 'strings:roomPage.imReady'})
            this.readyCheckboxContainer = createCheckboxIn(readyContainer, '', '', (e) => {
                e.preventDefault()
                UIRoomWarningController.runWithWarning(async(userAgreed?: boolean) => {
                    if(userAgreed) this.readyCheckboxContainer.checked = true
                    await UIGameSpriteHandler.continueAfterAssetsLoad()
                    socket.roomClient.setReadyToStart(this.readyCheckboxContainer.checked)
                }, WarningType.ReadyUpVPExceededRecommendedLimit, this.roomState)
            })
        }
    }

    private async onCopyLinkButtonClick(roomLink: string) {
        AudioManager.play(AudioType.Click)
        const url = new URL(roomLink)
        const cleanedRoomLink = `https://${url.host}/#${this.roomState.gameSetting.id}`
        UIMDeviceHandler.sendShareLink(cleanedRoomLink)

        this.onCopyButtonClick()
        const copyLinkEvent: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.CopiedRoomLink,
            category: AnalyticEventCategory.Lobby,
            stringValue: this.roomState.gameSetting.id,
        }
        void CA.sendAnalyticEventOnce(copyLinkEvent)
    }

    private onCopyButtonClick() {
        const innerHTML = this.copyLinkButton.innerHTML

        this.copyLinkButton.innerText = ''
        createDivIn(this.copyLinkButton, 'loading-icon')

        setTimeout(() => {
            // eslint-disable-next-line no-restricted-syntax
            this.copyLinkButton.innerHTML = innerHTML
        }, 2000)
    }

    private static areGuestsReadyToStart(roomState: RoomClientState): boolean {
        for(const guest of roomState.getGuestSessions()) {
            if(!guest.isReadyToPlay) return false
        }
        return true
    }

    private changeStartGameButtonState(roomState: RoomClientState) {
        if(!this.isHost) return
        if(UIMRoomPlayersController.areGuestsReadyToStart(roomState)) {
            this.startGameButton.classList.remove('m-button-disabled')
            this.startGameButton.innerText = 'Start Game'
            setElementOnClick(this.startGameButton, () => UIRoomWarningController.runWithWarning(async() => {
                AudioManager.play(AudioType.Click)
                await UIGameSpriteHandler.continueAfterAssetsLoad()
                socket.roomClient.startGame()
            }, WarningType.StartGameVPExceededRecommendedLimit, roomState))
            return
        }
        this.startGameButton.classList.add('m-button-disabled')
        this.startGameButton.innerText = 'Waiting for everyone to be ready...'
        setElementOnClick(this.startGameButton, () => undefined)
    }

    // eslint-disable-next-line class-methods-use-this
    private updateIAmReadyCheckbox(roomState: RoomClientState) {
        const readyCheckbox = document.querySelector('.m-room-players-ready-container input') as HTMLInputElement
        if(readyCheckbox == null) return
        for(const guest of roomState.getGuestSessions()) {
            if(socket.controller.session.id != guest.userSessionId) continue
            readyCheckbox.checked = guest.isReadyToPlay
        }
    }

    private handleSounds(roomState: RoomClientState) {
        const oldPlayerCount = this.playersCount
        const newPlayerCount = roomState.getSessionCount()
        if(oldPlayerCount > newPlayerCount) AudioManager.play(AudioType.Disconnect)
        if(oldPlayerCount < newPlayerCount) AudioManager.play(AudioType.JoinRoom)
    }

    update(roomState: RoomClientState) {
        this.roomState = roomState
        const isHost = roomState.isHost(socket.controller.session.id)
        const hostHasChanged = this.isHost != isHost
        if(hostHasChanged) {
            this.isHost = isHost
            this.renderBottomButtons()
        }
        this.handleSounds(roomState)
        this.changeStartGameButtonState(roomState)
        this.renderPlayerCards(roomState)
        this.updateIAmReadyCheckbox(roomState)
        this.userList.update(roomState)
        setElementOnClick(this.copyLinkButton, () => this.onCopyLinkButtonClick(window.location.href))
    }
}
